"use client";

/**
 * Box slider cards (acf.box_slider_section.box_slider repeater).
 * Fields: header, body, stacked (checkbox), and per slide:
 * background_colour, figure, icon, header, body, button.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import Slider from "react-slick";
import { TfiAngleLeft, TfiAngleRight } from "react-icons/tfi";
import { OpsiHtmlContent } from "../utils/OpsiHtmlContent";
import {
  getBoxSliderItems,
  getFigureText,
  getMediaUrl,
  hasHtmlContent,
  OPSI_PART_SPACING,
  OPSI_EXTERNAL_LINK_PROPS,
  resolveBackgroundColour,
  resolveOpsiUrl,
  splitSliderQuoteBody,
} from "../utils/opsiHelpers";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const OPSI_SLIDER_ACCENT = "#993189";
const OPSI_CARD_BG_FALLBACK = "#ffffff";
const OPSI_LARGE_CARD_BG_FALLBACK = "#F7F7F7";
const OPSI_SLIDER_CARD_GAP = 30;

const OpsiSliderCardButton = ({ href, text, className = "!mt-auto !pt-4" }) => {
  if (!href) {
    return null;
  }

  return (
    <Link
      href={href}
      {...OPSI_EXTERNAL_LINK_PROPS}
      className={`opsi-slider-card__button self-start transition hover:opacity-90 ${className}`}
      style={{ backgroundColor: OPSI_SLIDER_ACCENT }}
    >
      <span className="opsi-slider-card__button-label">{text}</span>
    </Link>
  );
};

const OpsiSliderNavButton = ({ direction, onClick, label, available = true }) => (
  <button
    type="button"
    aria-label={label}
    aria-disabled={!available}
    disabled={!available}
    onClick={onClick}
    className={`flex h-[48px] w-[48px] flex-none items-center justify-center rounded-full border-2 bg-[#fdf5f9] transition-opacity duration-300 ${
      available
        ? "cursor-pointer opacity-100 hover:bg-[#f7f7f7]"
        : "cursor-not-allowed opacity-40"
    }`}
    style={{ borderColor: OPSI_SLIDER_ACCENT }}
  >
    {direction === "prev" ? (
      <TfiAngleLeft size={18} color={OPSI_SLIDER_ACCENT} />
    ) : (
      <TfiAngleRight size={18} color={OPSI_SLIDER_ACCENT} />
    )}
  </button>
);

const OpsiLargeSliderCard = ({ item }) => {
  const buttonLink = resolveOpsiUrl(item?.button?.button_link);
  const buttonText = item?.button?.button_text || "View";
  const iconUrl = getMediaUrl(item?.icon);
  const cardBackground = resolveBackgroundColour(item, OPSI_LARGE_CARD_BG_FALLBACK);
  const hasHeader = hasHtmlContent(item?.header);
  const { quoteHtml, citationHtml } = splitSliderQuoteBody(item?.body);
  const isQuoteLayout = Boolean(iconUrl) || (!hasHeader && Boolean(citationHtml));

  return (
    <article
      className="opsi-slider-card opsi-slider-card--large flex h-full w-full max-w-[831px] flex-col rounded-[24px] border-2 p-10 md:p-12 max-[500px]:!p-6"
      style={{
        ["--opsi-slider-card-bg"]: cardBackground,
        borderColor: OPSI_SLIDER_ACCENT,
      }}
    >
      {isQuoteLayout ? (
        <>
          <div className="opsi-slider-card__quote-row flex flex-1 gap-5 md:gap-6">
            {iconUrl ? (
              <Image
                src={iconUrl}
                alt={item?.icon?.alt || "Quote"}
                width={item?.icon?.width || 72}
                height={item?.icon?.height || 55}
                className="opsi-slider-card__quote-icon h-auto w-[48px] object-contain mb-auto aspect-square flex-none md:w-[72px]"
              />
            ) : (
              <span
                className="opsi-slider-card__quote-mark flex-none text-[4rem] font-bold leading-none md:text-[5rem]"
                style={{ color: OPSI_SLIDER_ACCENT }}
                aria-hidden="true"
              >
                &ldquo;
              </span>
            )}

            {hasHtmlContent(quoteHtml) ? (
              <OpsiHtmlContent
                html={quoteHtml}
                className="opsi-slider-card__quote-text flex-1 [&_p:last-child]:!mb-0 [&_p]:!mb-4"
              />
            ) : null}
          </div>

          {hasHtmlContent(citationHtml) ? (
            <OpsiHtmlContent
              html={citationHtml}
              className="opsi-slider-card__citation !mt-8 md:!mt-10"
            />
          ) : null}
        </>
      ) : (
        <>
          {hasHeader ? (
            <OpsiHtmlContent
              html={item.header}
              as="h3"
              className="opsi-slider-card__header !m-0 !mb-4 !text-[1.35rem] !font-normal !text-[#000] md:!text-[1.5rem] [&_*]:!text-[#000]"
            />
          ) : null}

          {hasHtmlContent(item?.body) ? (
            <OpsiHtmlContent
              html={item.body}
              className="opsi-slider-card__body text-[1.25rem] !font-normal !text-[#464646] leading-relaxed [&_*]:!text-[#464646] [&_p]:!mb-4 [&_p:last-child]:!mb-0 max-[500px]:!text-[1rem]"
            />
          ) : null}
        </>
      )}

      {buttonLink ? (
        <OpsiSliderCardButton
          href={buttonLink}
          text={buttonText}
          className="!mt-auto !pt-6"
        />
      ) : null}
    </article>
  );
};

const OpsiSliderCard = ({ item, variant = "box", isStacked = false }) => {
  if (variant === "large") {
    return <OpsiLargeSliderCard item={item} />;
  }

  const buttonLink = resolveOpsiUrl(item?.button?.button_link);
  const buttonText = item?.button?.button_text || "View";
  const iconUrl = getMediaUrl(item?.icon);
  const figureText = getFigureText(item?.figure);
  const cardBackground = resolveBackgroundColour(item, OPSI_CARD_BG_FALLBACK);
  const hasIconAndFigure = Boolean(iconUrl && figureText);
  const useStackedIconFigureLayout = isStacked && hasIconAndFigure;

  return (
    <article
      className={`opsi-slider-card opsi-slider-card--box flex h-full w-full flex-col rounded-[20px] !p-[2rem] max-[850px]:!p-[1.5rem] max-[720px]:!max-h-[500px] ${
        useStackedIconFigureLayout
          ? "max-w-none pr-[46px]"
          : "pr-20"
      }`}
      style={{
        ["--opsi-slider-card-bg"]: cardBackground,
      }}
    >
      {useStackedIconFigureLayout ? (
        <div className="flex w-full items-start justify-between gap-4">
          <p
            className="!m-0 !text-[4rem] !font-bold leading-none md:!text-[4.5rem]"
            style={{ color: OPSI_SLIDER_ACCENT }}
          >
            {figureText}
          </p>
          <Image
            src={iconUrl}
            alt={item?.icon?.alt || ""}
            width={item?.icon?.width || 80}
            height={item?.icon?.height || 80}
            className="h-[48px] w-[48px] flex-none object-contain md:h-[80px] md:w-[80px]"
          />
        </div>
      ) : (
        <>
          {iconUrl ? (
            <Image
              src={iconUrl}
              alt={item?.icon?.alt || ""}
              width={item?.icon?.width || 80}
              height={item?.icon?.height || 80}
              className="mb-4 h-[48px] w-[48px] flex-none object-contain md:h-[80px] md:w-[80px]"
            />
          ) : null}

          {figureText ? (
            <p
              className="!m-0 !text-[4rem] !font-bold leading-none md:!text-[4.5rem]"
              style={{ color: OPSI_SLIDER_ACCENT }}
            >
              {figureText}
            </p>
          ) : null}
        </>
      )}

      {hasHtmlContent(item?.header) ? (
        <OpsiHtmlContent
          html={item.header}
          as="h3"
          className="opsi-slider-card__header !m-0 !mt-4 !text-[1.3rem] !font-[500] !text-[#000] leading-tight md:!text-[1.3rem] [&_*]:!text-[#000]"
        />
      ) : null}

      {hasHtmlContent(item?.body) ? (
        <OpsiHtmlContent
          html={item.body}
          className={`opsi-slider-card__body py-5 !mt-3 min-h-0 flex-1 !text-[1rem] !font-medium !text-[#464646] leading-snug [&_*]:!text-[#464646] [&_li]:!mb-2 [&_p]:!m-0 [&_ul]:!mb-0 [&_ul]:!list-disc max-[420px]:p-0 ${
            useStackedIconFigureLayout ? "px-0" : "px-[15px]"
          }`}
        />
      ) : null}

      {buttonLink ? (
        <OpsiSliderCardButton href={buttonLink} text={buttonText} />
      ) : null}
    </article>
  );
};

export const OpsiBoxSlider = ({
  data,
  variant = "box",
  className = `opsi-box-slider overflow-x-clip ${OPSI_PART_SPACING}`,
}) => {
  const sliderRef = useRef(null);
  const carouselRef = useRef(null);
  const items = getBoxSliderItems(data);
  const header = data?.header ?? "";
  const body = data?.body ?? "";
  const isStacked = data?.stacked === true;
  const isLarge = variant === "large";
  const isSingleItem = items.length === 1;
  const boxDesktopSlidesToShow = !isStacked && items.length >= 3 ? 2.25 : 2;
  const defaultSlidesToShow = isLarge || isSingleItem ? 1 : boxDesktopSlidesToShow;
  const [slidesToShow, setSlidesToShow] = useState(defaultSlidesToShow);
  const showNav = items.length > slidesToShow;
  const [canGoPrev, setCanGoPrev] = useState(false);
  const [canGoNext, setCanGoNext] = useState(items.length > defaultSlidesToShow);

  const updateNavState = useCallback(() => {
    const slider = sliderRef.current?.innerSlider;

    if (!slider?.state) {
      return;
    }

    const { currentSlide, slideCount } = slider.state;
    const { slidesToShow: currentSlidesToShow, infinite } = slider.props;

    setSlidesToShow(currentSlidesToShow);
    setCanGoPrev(currentSlide > 0);

    if (infinite || slideCount == null) {
      setCanGoNext(slideCount == null ? items.length > defaultSlidesToShow : true);
      return;
    }

    setCanGoNext(
      slideCount > currentSlidesToShow &&
        currentSlide < slideCount - currentSlidesToShow,
    );
  }, [defaultSlidesToShow, items.length]);

  const equalizeCardHeights = useCallback(() => {
    if (isSingleItem || isStacked) {
      return;
    }

    const list = sliderRef.current?.innerSlider?.list;

    if (!list) {
      return;
    }

    const slides = list.querySelectorAll(".slick-slide:not(.slick-cloned)");
    const measured = [];

    slides.forEach((slide) => {
      const card = slide.querySelector(".opsi-slider-card");
      const wrapper = slide.querySelector(".opsi-box-slider__slide");

      if (!card || !wrapper) {
        return;
      }

      card.style.height = "auto";
      wrapper.style.height = "auto";
      measured.push({ card, wrapper });
    });

    let maxHeight = 0;

    measured.forEach(({ card }) => {
      maxHeight = Math.max(maxHeight, card.getBoundingClientRect().height);
    });

    if (!maxHeight) {
      return;
    }

    measured.forEach(({ card, wrapper }) => {
      wrapper.style.height = `${maxHeight}px`;
      card.style.height = "100%";
    });
  }, [isSingleItem, isStacked]);

  const handleSliderUpdate = useCallback(() => {
    updateNavState();
    requestAnimationFrame(() => {
      equalizeCardHeights();
    });
  }, [equalizeCardHeights, updateNavState]);

  useEffect(() => {
    if (isStacked) {
      return undefined;
    }

    const frame = requestAnimationFrame(() => {
      equalizeCardHeights();
    });

    const carousel = carouselRef.current;

    if (!carousel || typeof ResizeObserver === "undefined") {
      return () => cancelAnimationFrame(frame);
    }

    const observer = new ResizeObserver(() => {
      equalizeCardHeights();
    });

    observer.observe(carousel);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [equalizeCardHeights, isStacked, items.length, variant]);

  if (
    !items.length &&
    !hasHtmlContent(header) &&
    !hasHtmlContent(body)
  ) {
    return null;
  }

  const settings = {
    dots: false,
    arrows: false,
    infinite: false,
    speed: 450,
    slidesToScroll: 1,
    slidesToShow: defaultSlidesToShow,
    afterChange: handleSliderUpdate,
    onInit: handleSliderUpdate,
    onReInit: handleSliderUpdate,
    responsive: [
      {
        breakpoint: 1300,
        settings: {
          slidesToShow: isLarge || isSingleItem ? 1 : 2,
        },
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const sectionModifiers = [
    isSingleItem ? "opsi-box-slider--single" : "",
    isStacked ? "opsi-box-slider--stacked" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      className={`${className}${sectionModifiers ? ` ${sectionModifiers}` : ""}`}
      data-opsi-part={`box-slider-${variant}`}
    >
      {hasHtmlContent(header) ? (
        <OpsiHtmlContent
          html={header}
          as="h2"
          className="opsi-box-slider__header !mb-4 !text-[1.5rem] !font-bold !text-[#993189] md:!text-[1.75rem] [&_*]:!text-[#993189] [&_*]:!text-[#993189]"
        />
      ) : null}
      {hasHtmlContent(body) ? (
        <OpsiHtmlContent
          html={body}
          className="opsi-box-slider__intro !mb-6 !text-[1rem] !text-[#464646] leading-relaxed md:!text-[1.125rem] [&_*]:!text-[#464646]"
        />
      ) : null}

      {items.length ? (
        isStacked ? (
          <div
            className="opsi-box-slider__stack flex w-full flex-col items-center"
            style={{ gap: `${OPSI_SLIDER_CARD_GAP}px` }}
          >
            {items.map((item, index) => (
              <div
                key={`${item?.header || item?.figure || "card"}-${index}`}
                className="opsi-box-slider__slide"
              >
                <OpsiSliderCard item={item} variant={variant} isStacked />
              </div>
            ))}
          </div>
        ) : (
          <div ref={carouselRef} className="opsi-box-slider__carousel w-full">
            <Slider ref={sliderRef} {...settings} className="opsi-box-slider__slick">
              {items.map((item, index) => (
                <div
                  key={`${item?.header || item?.figure || "card"}-${index}`}
                  className="opsi-box-slider__slide"
                >
                  <OpsiSliderCard item={item} variant={variant} />
                </div>
              ))}
            </Slider>

            {showNav ? (
              <div className="opsi-box-slider__nav ml-auto !mt-[18px] flex items-end justify-end gap-3">
                <OpsiSliderNavButton
                  direction="prev"
                  label="Previous slide"
                  available={canGoPrev}
                  onClick={() => sliderRef.current?.slickPrev()}
                />
                <OpsiSliderNavButton
                  direction="next"
                  label="Next slide"
                  available={canGoNext}
                  onClick={() => sliderRef.current?.slickNext()}
                />
              </div>
            ) : null}
          </div>
        )
      ) : null}

      <style jsx global>{`
        .opsi-box-slider__slick .slick-list {
          overflow: visible;
          margin-left: 0 !important;
          padding-left: 0 !important;
        }

        .opsi-box-slider__slick .slick-track {
          display: flex !important;
          align-items: stretch;
          margin-left: 0 !important;
          margin-right: 0 !important;
          justify-content: flex-start;
        }

        .opsi-box-slider__slick .slick-slide {
          float: none !important;
          display: flex !important;
          height: auto !important;
          align-self: stretch;
          box-sizing: border-box;
          background: transparent !important;
        }

        .opsi-box-slider__slick .slick-slide > div {
          display: flex;
          flex: 1 1 auto;
          width: 100%;
          height: 100%;
          min-height: 100%;
          background: transparent !important;
        }

        .opsi-box-slider__slide {
          box-sizing: border-box;
          container-type: inline-size;
          display: flex;
          flex: 1 1 auto;
          height: 100%;
          min-height: 100%;
          padding-right: ${OPSI_SLIDER_CARD_GAP}px;
          width: 100%;
        }

        .opsi-slider-card {
          background-color: #f7f7f7 !important;
          width: 100%;
          max-width: 100%;
          height: 100%;
          box-sizing: border-box;
        }

        .opsi-box-slider__slick .slick-slide:last-child .opsi-box-slider__slide {
          padding-right: 0;
        }

        .opsi-box-slider--single .opsi-box-slider__slick .slick-track {
          align-items: flex-start;
        }

        .opsi-box-slider--single .opsi-box-slider__slick .slick-slide {
          align-self: flex-start;
          height: auto !important;
        }

        .opsi-box-slider--single .opsi-box-slider__slick .slick-slide > div {
          height: auto;
          min-height: 0;
        }

        .opsi-box-slider--single .opsi-box-slider__slide {
          height: auto;
          min-height: 0;
        }

        .opsi-box-slider--single .opsi-slider-card {
          height: auto !important;
        }

        .opsi-box-slider--single .opsi-slider-card__body,
        .opsi-box-slider--single .opsi-slider-card__quote-row,
        .opsi-box-slider--stacked .opsi-slider-card__body,
        .opsi-box-slider--stacked .opsi-slider-card__quote-row {
          flex: 0 1 auto !important;
        }

        .opsi-box-slider--stacked .opsi-box-slider__slide {
          height: auto;
          min-height: 0;
          padding-right: 0;
          width: 75%;
        }

        .opsi-box-slider--stacked .opsi-slider-card {
          height: auto !important;
        }

        /* 4/2 minimum proportion; card grows taller when content needs it */
        .opsi-slider-card--box {
          min-height: max(200px, 50cqi);
        }

        .opsi-slider-card--large {
          min-height: 280px;
        }

        .opsi-box-slider--single .opsi-slider-card--box,
        .opsi-box-slider--single .opsi-slider-card--large,
        .opsi-box-slider--stacked .opsi-slider-card--box,
        .opsi-box-slider--stacked .opsi-slider-card--large {
          min-height: 0;
        }

        .opsi-slider-card--large .opsi-slider-card__quote-text,
        .opsi-slider-card--large .opsi-slider-card__quote-text p,
        .opsi-slider-card--large .opsi-slider-card__quote-text strong {
          color: #000 !important;
          font-size: 1.125rem;
          font-weight: 700;
          line-height: 1.5;
        }

        .opsi-slider-card--large .opsi-slider-card__citation,
        .opsi-slider-card--large .opsi-slider-card__citation p {
          color: #464646 !important;
          font-size: 0.9375rem;
          font-weight: 400 !important;
          line-height: 1.45;
        }

        .opsi-slider-card__body,
        .opsi-slider-card__body * {
          color: #000 !important;
        }

        .opsi-box-slider__header,
        .opsi-box-slider__header * {
          color: #000 !important;
        }

        .opsi-box-slider__intro,
        .opsi-box-slider__intro * {
          color: #000 !important;
        }

        .opsi-slider-card__button {
          align-items: center !important;
          background-color: ${OPSI_SLIDER_ACCENT} !important;
          box-sizing: border-box !important;
          color: #fff !important;
          display: inline-grid !important;
          font-size: 1rem !important;
          font-weight: 600 !important;
          height: 44px !important;
          justify-items: center !important;
          line-height: 1 !important;
          margin: 0 !important;
          min-width: 100px !important;
          padding: 0 2rem !important;
          place-items: center !important;
          border-radius: 9999px !important;
          text-decoration: none !important;
        }

        .opsi-slider-card__button-label {
          display: block !important;
          line-height: 1 !important;
          margin: 0 !important;
          padding: 0 !important;
        }


        @media (max-width: 500px) {
           .opsi-box-slider--stacked .opsi-box-slider__slide
          {
              width: 100% !important;
          }
        }
      `}</style>
    </section>
  );
};
